import React from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
  FiMail,
  FiMapPin,
  FiPhone,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AssociationStatusBadge from "./AssociationStatusBadge";

import {
  formatDateTime,
  getGoogleMapsUrl,
  normalizeExternalUrl,
  truncateText,
} from "../utils/associationsAdminUtils";

export default function AssociationCard({
  item,
  actionId,
  onPreview,
  onEdit,
  onToggle,
  onAskDelete,
}) {
  const logoUrl = item?.logo_asso ? toAbsolutePublicUrl(item.logo_asso) : "";
  const toggleLoading = actionId === `toggle-${item.id}`;
  const mapsUrl = getGoogleMapsUrl(item.gps_asso);

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="flex min-h-full flex-col p-4 sm:p-5">
        <div className="flex gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[1.4rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={item.nom_asso}
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <FiImage className="text-3xl text-slate-300 dark:text-slate-600" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <AssociationStatusBadge active={!!item.is_active} />

              {item.categorie ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                  <FiUsers />
                  {item.categorie}
                </span>
              ) : null}

              {item.gps_asso ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                  <FiMapPin />
                  GPS
                </span>
              ) : null}
            </div>

            <h3 className="mt-3 line-clamp-2 text-lg font-bold leading-snug text-slate-950 dark:text-white">
              {item.nom_asso || "Association sans nom"}
            </h3>

            {item.contact_asso ? (
              <p className="mt-1 line-clamp-1 text-sm font-bold text-slate-500 dark:text-slate-400">
                {item.contact_asso}
                {item.fonction_asso ? ` — ${item.fonction_asso}` : ""}
              </p>
            ) : null}
          </div>
        </div>

        <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {truncateText(item.description_asso, 220) ||
            "Aucune description renseignée."}
        </p>

        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          {item.tel_asso ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiPhone />
              {item.tel_asso}
            </span>
          ) : null}

          {item.mail_asso ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiMail />
              {item.mail_asso}
            </span>
          ) : null}

          {item.website_asso ? (
            <a
              href={normalizeExternalUrl(item.website_asso)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1.5 text-sky-700 transition hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300"
            >
              <FiExternalLink />
              Site web
            </a>
          ) : null}

          {mapsUrl ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700 transition hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300"
            >
              <FiMapPin />
              Carte
            </a>
          ) : null}
        </div>

        <p className="mt-4 text-xs font-bold text-slate-400 dark:text-slate-500">
          Créée le {formatDateTime(item.created_at)}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          <AdminButton
            icon={FiEye}
            variant="secondary"
            size="sm"
            onClick={() => onPreview(item.id)}
          >
            Voir
          </AdminButton>

          <AdminButton
            icon={FiEdit2}
            variant="soft"
            size="sm"
            onClick={() => onEdit(item.id)}
          >
            Modifier
          </AdminButton>

          <AdminButton
            icon={item.is_active ? FiToggleLeft : FiToggleRight}
            variant={item.is_active ? "secondary" : "primary"}
            size="sm"
            loading={toggleLoading}
            onClick={() => onToggle(item.id)}
          >
            {item.is_active ? "Masquer" : "Publier"}
          </AdminButton>

          <AdminButton
            icon={FiTrash2}
            variant="dangerSoft"
            size="sm"
            onClick={() => onAskDelete(item.id)}
          >
            Supprimer
          </AdminButton>
        </div>
      </div>
    </article>
  );
}